import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Send } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    event_type: "",
    preferred_date: "",
    message: "",
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setForm({ ...form, preferred_date: date ? format(date, "yyyy-MM-dd") : "" });
    setCalendarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Booking request sent successfully");
      setForm({ name: "", email: "", event_type: "", preferred_date: "", message: "" });
      setSelectedDate(null);
      onOpenChange(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error("Too many requests. Please try again later.");
      else if (status === 422) toast.error("Please check your name and email address.");
      else toast.error("Failed to send booking request");
    } finally {
      setSubmitting(false);
    }
  };

  const eventTypes = ["Club Night", "Festival", "Private Event", "Corporate", "Wedding", "Other"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tighter">BOOK ALCHEMIST</DialogTitle>
          <DialogDescription className="font-sub text-[10px] text-white/40 tracking-widest mt-2">
            FILL IN THE DETAILS BELOW AND WE WILL GET BACK TO YOU
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          {/* Name */}
          <div>
            <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-3 block">YOUR NAME</Label>
            <Input
              data-testid="booking-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-white placeholder:text-white/15 focus-visible:ring-0 focus-visible:border-white"
              placeholder="Full name"
              required
            />
          </div>

          {/* Email */}
          <div>
            <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-3 block">EMAIL</Label>
            <Input
              data-testid="booking-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-white placeholder:text-white/15 focus-visible:ring-0 focus-visible:border-white"
              placeholder="your@email.com"
              required
            />
          </div>

          {/* Event Type */}
          <div>
            <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-3 block">EVENT TYPE</Label>
            <div className="flex flex-wrap gap-2">
              {eventTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  data-testid={`event-type-${type.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setForm({ ...form, event_type: type })}
                  className={`font-sub text-[10px] tracking-widest py-2 px-4 border transition-colors duration-300 ${
                    form.event_type === type
                      ? "border-white/40 bg-white/10 text-white"
                      : "border-white/10 text-white/30 hover:border-white/20 hover:text-white/50"
                  }`}
                >
                  {type.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Preferred Date with Calendar */}
          <div>
            <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-3 block">PREFERRED DATE</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="booking-date-picker"
                  className="w-full text-left bg-transparent border-0 border-b border-white/20 px-0 py-4 text-sm transition-colors duration-300 hover:border-white/40 flex items-center justify-between"
                >
                  <span className={selectedDate ? "text-white" : "text-white/15"}>
                    {selectedDate ? format(selectedDate, "MMMM d, yyyy") : "Select a date"}
                  </span>
                  <CalendarIcon className="w-4 h-4 text-white/30" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#0f0f0f] border-white/10" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => date < new Date()}
                  className="text-white"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Message */}
          <div>
            <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-3 block">MESSAGE</Label>
            <Textarea
              data-testid="booking-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="bg-transparent border border-white/10 rounded-none px-3 py-4 text-white placeholder:text-white/15 focus-visible:ring-0 focus-visible:border-white/30 min-h-[100px] resize-none"
              placeholder="Tell us about your event, expected attendance, location..."
            />
          </div>

          {/* Submit */}
          <button
            data-testid="booking-submit-btn"
            type="submit"
            disabled={submitting}
            className="btn-alchemist w-full border border-white/25 bg-white/5 hover:bg-white hover:text-black py-4 px-8 uppercase tracking-widest text-xs font-sub transition-colors duration-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-3 mt-4"
          >
            <Send className="w-3 h-3" />
            {submitting ? "Sending..." : "Send Request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
